// Shared state shape + reducer actions used by both the quoter (apiContext) and
// the favorite-plan template form (MyFavoritePlanFormContext), so the two forms
// stay structurally interchangeable for shared steps and for transformData/pricing.

export const CHANGE_DATA = "quote-form-change_data";
export const CHANGE_BENEFIT = "quote-form-change_benefit";
export const CHANGE_BENEFIT_NAME = "quote-form-change_benefit_name";
export const CHANGE_BENEFIT_ID = "quote-form-change_benefit_id";
export const REMOVE_BENEFITS = "quote-form-remove_benefits";
export const ADD_CALCULATION = "quote-form-add_calculation";
export const SET_CALCULATION_ERROR = "quote-form-set_calculation_error";
export const POPULATE_CONTEXT = "quote-form-populate_context";
export const LOAD_DATA = "quote-form-load_data";

/**
 * @param {Record<string, any>} [overrides] - extra/override fields a specific
 * form needs (e.g. the quoter's dependantData scratch object, wizard step index).
 */
export const createQuoteFormInitialState = (overrides = {}) => ({
    name: "",
    surname: "",
    email: "",
    gender: 0,
    country: 0,
    coverage: "",
    coverageShow: "",
    planId: "152",
    birthday: "",
    birthdayShow: "",
    travel: false,
    addDependant: false,
    selectedBenefits: [],
    selectedBenefitsNames: [],
    selectedBenefitsIds: [],
    dependants: [],
    organ: true,
    organRider: 0,
    organId: 0,
    outpationBenefitsEnabled: false,
    outpationBenefitsEnabled4fases: false,
    outpationBenefitsmaternidad: false,
    step14Confirmation: false,
    organRiderName: "No",
    timely: "annual",
    acceptNonRegion: false,
    acceptNonRegion2: false,
    limitedCover: false,
    nonAmbulance: false,
    nonMaternidad: false,
    dependantData: {
        name: "",
        surname: "",
        email: "",
        gender: 0,
        country: 0,
        birthday: "",
        birthdayShow: "",
        componentId: "",
        travel: false
    },
    calculation: {},
    // True when the last pricing request failed after a calculation had already
    // succeeded once - the UI keeps showing `calculation` (the latest known-good
    // estimate) but flags it as stale next to the price.
    calculationError: false,
    isCountryResidenceDeductible: false,
    hasLoadedQuoteContext: false,
    isCountryResidenceDeductibleFlagPresent: false,
    // Id del plan favorito ("My Favorite Plan") del que arranca la cotización cuando se usa
    // un template desde el dashboard. `null` en cotizaciones normales; se manda a
    // `/rate-engine/compute` para rastrear el template de origen (ver transformData).
    myFavoritePlanId: null,
    ...overrides,
});

/**
 * Base reducer handling the state shape shared by every quote-shaped form
 * (personal info fields, benefit selection, calculation). Forms with extra
 * concerns (e.g. the quoter's dependant-editing flow) should wrap this in
 * their own switch and fall through to it on `default`.
 */
export const quoteFormReducer = (state, action) => {
    switch (action.type) {
        case LOAD_DATA:
            return action.payload;
        case CHANGE_DATA:
            return { ...state, [action.payload.name]: action.payload.value };
        case CHANGE_BENEFIT:
            return { ...state, selectedBenefits: { ...state.selectedBenefits, [action.payload.name]: action.payload.value } };
        case CHANGE_BENEFIT_NAME:
            return { ...state, selectedBenefitsNames: { ...state.selectedBenefitsNames, [action.payload.name]: action.payload.value } };
        case CHANGE_BENEFIT_ID:
            return { ...state, selectedBenefitsIds: { ...state.selectedBenefitsIds, [action.payload.name]: action.payload.value } };
        case REMOVE_BENEFITS: {
            const { [action.payload.name]: _removedBenefit, ...remainingBenefits } = state.selectedBenefits;
            const { [action.payload.name]: _removedName, ...remainingNames } = state.selectedBenefitsNames;
            const { [action.payload.name]: _removedId, ...remainingIds } = state.selectedBenefitsIds;
            return {
                ...state,
                selectedBenefits: remainingBenefits,
                selectedBenefitsNames: remainingNames,
                selectedBenefitsIds: remainingIds,
            };
        }
        case ADD_CALCULATION:
            return { ...state, calculation: action.payload || {}, calculationError: false };
        case SET_CALCULATION_ERROR: {
            const previousPrice = +state.calculation?.data?.quotation?.totalPremiumWithFee || 0;
            return previousPrice !== 0 ? { ...state, calculationError: true } : state;
        }
        case POPULATE_CONTEXT: {
            const payload = action.payload || {};
            const isCountryResidenceDeductibleFlagPresent = Object.prototype.hasOwnProperty.call(payload, 'isCountryResidenceDeductible');

            return {
                ...state,
                ...payload,
                hasLoadedQuoteContext: true,
                isCountryResidenceDeductibleFlagPresent,
            };
        }
        default:
            return state;
    }
};
